import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { ApiError } from "@/lib/api/index";
import { tickers as api } from "@/lib/api/tickers";
import type { DiscoverResponse } from "@/lib/api/tickers";
import { hydrateRegistry, registerTicker } from "@/lib/tickers/registry";
import { useAuth } from "@/hooks/useAuth";
import {
  subscribeMarketData,
  subscribeOptionChain,
  useUserChannel,
} from "@/hooks/useUserChannel";
import type { TickerConfig } from "@/types/ticker";

// Hydrates the runtime ticker registry from /api/v1/tickers on first
// authenticated mount, then subscribes to UserChannel `ticker.discovered`
// broadcasts so newly auto-added tickers appear in the picker without
// a full page reload. The compile-time `BUILT_IN_TICKERS` is the
// first-paint fallback; this provider extends it with anything Yahoo
// has surfaced through the discover endpoint.
//
// Cable scope is intentionally per-user (UserChannel is owned by the
// signed-in user). Inactive tabs catch up on next picker open via the
// next /tickers fetch — discovery is low-frequency, so we don't need
// global broadcast plumbing.

// ── Hydration tracking ────────────────────────────────────────────────
// A freshly :discovered ticker has the row + seed quote at request time
// (Quotes::Ingester runs inside Tickers::Discoverer), but the option
// chain and the day's intraday bars are filled by the async
// TickerHydrationJob — typically within seconds, but the gap is real.
// While hydrating, components should render a loading affordance instead
// of the steady-state empty view. The set is cleared when the first
// OptionChainChannel broadcast lands for that symbol, with a 30s safety
// fallback in case the Worker fetch fails.
const hydratingTickers = new Set<string>();
const hydrationListeners = new Set<() => void>();

function notifyHydration() {
  hydrationListeners.forEach((cb) => cb());
}

function subscribeHydration(cb: () => void): () => void {
  hydrationListeners.add(cb);
  return () => hydrationListeners.delete(cb);
}

function getHydratingSnapshot(): ReadonlySet<string> {
  return hydratingTickers;
}

function markHydrating(symbol: string) {
  if (hydratingTickers.has(symbol)) return;
  hydratingTickers.add(symbol);
  notifyHydration();

  // Open the per-ticker MarketDataChannel subscription up-front so the
  // Hydrator's intraday burst (fires from Sidekiq within ~1s of the
  // discover response) lands on a live subscriber and renders
  // tick-by-tick into the chart. Under v1 the global channel made this
  // implicit; under v2 the per-ticker split means the FE must subscribe
  // before broadcasts begin, and the discover-callback site is the
  // earliest point where we know the symbol is real and active.
  const detachMarket = subscribeMarketData(symbol, () => {});

  // Clear on the first chain broadcast for this symbol — that's the
  // last data plane to fill (quote + intraday + chain). The cable
  // subscription is one-shot and tears itself down.
  const safetyTimer = setTimeout(() => {
    if (hydratingTickers.delete(symbol)) notifyHydration();
    unsub();
    detachMarket();
  }, 30_000);

  const unsub = subscribeOptionChain(symbol, () => {
    if (hydratingTickers.delete(symbol)) notifyHydration();
    clearTimeout(safetyTimer);
    unsub();
    detachMarket();
  });
}

export function useTickerHydrating(symbol: string | null): boolean {
  return useSyncExternalStore(
    subscribeHydration,
    () => (symbol ? getHydratingSnapshot().has(symbol) : false),
    () => false,
  );
}

export function __resetHydratingForTests(): void {
  hydratingTickers.clear();
  hydrationListeners.clear();
}

// Hook callers don't get the raw ApiError — the wrapper folds 404/429
// and other failure modes into the typed result shape so components can
// `switch (res.status)` without importing API internals.
export type DiscoverResult =
  | { status: "ready" | "discovered"; ticker: TickerConfig }
  | { status: "unsupported"; reason?: string }
  | { status: "invalid"; reason?: string }
  | { status: "rate_limited" }
  | { status: "error" };

interface TickerRegistryContextValue {
  discover: (symbol: string) => Promise<DiscoverResult>;
}

const TickerRegistryContext = createContext<TickerRegistryContextValue>({
  discover: () => Promise.resolve({ status: "error" }),
});

export function TickerRegistryProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const { subscribe } = useUserChannel();

  useEffect(() => {
    if (status !== "authenticated") return;

    let cancelled = false;
    api
      .listTickers()
      .then((rows) => {
        if (cancelled) return;
        hydrateRegistry(rows.map(toTickerConfig));
      })
      .catch((err: unknown) => {
        // Non-fatal — compile-time BUILT_IN_TICKERS keeps the picker working.

        console.warn("[TickerRegistryProvider] hydrate failed:", err);
      });

    const unsubscribe = subscribe((message) => {
      if (message.event !== "ticker.discovered") return;
      const payload = message.ticker as TickerConfig | undefined;
      if (!payload) return;
      registerTicker(toTickerConfig(payload));
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [status, subscribe]);

  const discover = useCallback(
    async (symbol: string): Promise<DiscoverResult> => {
      let res: DiscoverResponse;
      try {
        res = await api.discoverTicker(symbol);
      } catch (err) {
        if (err instanceof ApiError && err.status === 429)
          return { status: "rate_limited" };
        if (err instanceof ApiError && err.status === 404)
          return { status: "unsupported" };
        console.warn("[TickerRegistryProvider] discover failed:", err);
        return { status: "error" };
      }

      if (
        (res.status === "ready" || res.status === "discovered") &&
        res.ticker
      ) {
        const cfg = toTickerConfig(res.ticker);
        registerTicker(cfg);
        if (res.status === "discovered") markHydrating(cfg.symbol);
        return { status: res.status, ticker: cfg };
      }
      if (res.status === "unsupported")
        return { status: "unsupported", reason: res.reason };
      if (res.status === "invalid")
        return { status: "invalid", reason: res.reason };
      return { status: "error" };
    },
    [],
  );

  const value = useMemo(() => ({ discover }), [discover]);

  return (
    <TickerRegistryContext.Provider value={value}>
      {children}
    </TickerRegistryContext.Provider>
  );
}

export function useTickerDiscover() {
  return useContext(TickerRegistryContext).discover;
}

function toTickerConfig(row: TickerConfig): TickerConfig {
  return {
    symbol: row.symbol,
    display_name: row.display_name,
    kind: row.kind,
    strike_step: Number(row.strike_step),
    vol_index_symbol: row.vol_index_symbol,
    expiry_kind: row.expiry_kind,
    yahoo_symbol: row.yahoo_symbol,
    active: row.active,
  };
}
